import { Button, ButtonProps, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconTrash as IconDelete } from '@tabler/icons-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface DeleteButtonProps extends ButtonProps {
  onDelete: () => Promise<void> | Promise<unknown>;
  isDeleting?: boolean;

  label?: string;
  confirmTitle?: string;
  confirmContent?: string;
}

export function DeleteButton(props: DeleteButtonProps) {
  const { onDelete, isDeleting, label, confirmTitle, confirmContent, ...buttonProps } = props;
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const isRunningRef = useRef(false);
  const handleDelete = () => {
    if (isRunningRef.current) {
      return;
    }
    isRunningRef.current = true;
    let deletion: Promise<unknown>;
    try {
      deletion = onDelete();
    } catch (error) {
      isRunningRef.current = false;
      throw error;
    }
    // Callers report the failure through the mutation's onError, so a rejection only keeps the dialog open.
    deletion
      .then(close, () => undefined)
      .finally(() => {
        isRunningRef.current = false;
      });
  };

  return (
    <>
      <Button
        {...buttonProps}
        onClick={open}
        bg="red"
        leftSection={<IconDelete size={16} />}
      >
        {label ?? t('button.delete')}
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={confirmTitle ?? t('deleteConfirm.title')}
        centered
      >
        <Stack>
          <Text
            style={{ textAlign: 'center' }}
            fw="500"
          >
            {confirmContent ?? t('deleteConfirm.content')}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={close}
              loading={isDeleting}
            >
              {t('button.cancel')}
            </Button>
            <Button
              onClick={handleDelete}
              bg="red"
              loading={isDeleting}
              leftSection={<IconDelete size={16} />}
            >
              {t('button.delete')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
